import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Project, Change, Client } from '../../models';
import { Modal} from 'flowbite-react';
import { Field, Formik, Form } from 'formik';
import { RootState, useAppSelector, useAppDispatch } from '../../redux';
import { startCreateChange, createChange, loadProject, loadProjects, loadBoard, loadClients, loadUsers } from '../../redux';
import { SelectField, Button, Icon, LabelWithHelp, ClientSelectField, EmailSuggestField } from '../../components';
import {
    isValidEmail,
    loadQuickCreatePlacement,
    parseRequesterEmails,
    saveQuickCreatePlacement,
    type QuickCreateMode,
} from '../../utils/quickCreatePrefs';
import { buildRecipientEmailSources } from '../../utils/recipientEmailSources';
import {
    matchingProjectsForClient,
    projectSelectOptions,
} from '../../utils/projectSelectOptions';

interface QuickChangeModalProps {
    show: boolean;
    onClose?: () => void;
    onAdded?: (change: Change) => void;
    /** Prefill support requester chips when opening the modal. */
    initialRequesterEmails?: string[];
    /** Prefill / force the billing client on create. */
    initialClient?: Client;
    /** Force create mode (e.g. support from Demandeur view). */
    initialMode?: QuickCreateMode;
}

const modalTheme = {
    root: {
        // Keep scrolling inside the modal body on small screens so the mode tabs
        // (and header) are not scrolled off-screen with the overlay.
        base: 'fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-x-hidden overflow-y-hidden md:inset-0 md:h-full',
    },
    content: {
        // Default theme uses h-full below md, which forces the overlay to scroll.
        base: 'relative w-full p-4 h-auto',
        // Avoid fixed min-height on small viewports: min-h-[560px] + max-h-[90vh]
        // clips the top of the modal (mode tabs) when 90vh < 560px.
        inner: 'relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col min-h-0 h-auto max-h-[90vh] w-full sm:min-h-[560px]',
    },
    body: {
        base: 'flex-1 overflow-y-auto min-h-0',
        popup: 'pt-0',
    },
};
const headerTheme = {
    "title": "text-lg font-medium text-white dark:text-white w-full",
    "close": {
        "base": "ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-white hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
    }
};

function QuickChangeModal(props: QuickChangeModalProps) {
    const {
        show, onClose, onAdded, initialRequesterEmails, initialClient, initialMode: forcedMode,
    } = props;

    const { t }                 = useTranslation();
    const { project, projects } = useAppSelector((state : RootState) => state.projects);
    const { user }              = useAppSelector((state : RootState) => state.auth);

    const { board }                        = useAppSelector((state : RootState) => state.boards);
    const { creating, created, change, changes, triageChanges, reviewChanges } = useAppSelector((state : RootState) => state.changes);
    const { clients }                      = useAppSelector((state : RootState) => state.clients);
    const { users, loading: usersLoading } = useAppSelector((state : RootState) => state.users);
    const remembered                       = React.useMemo(() => loadQuickCreatePlacement(), []);
    const initialMode                      = forcedMode || remembered.mode || 'card';
    const [projectId, setProjectId]        = useState(remembered.projectId || project?._id || '');
    // Support mode always uses the first board/column — do not restore remembered placement.
    const [boardId, setBoardId]            = useState(initialMode === 'support' ? '' : (remembered.boardId || ''));
    const [columnId, setColumnId]          = useState(initialMode === 'support' ? '' : (remembered.columnId || ''));
    const [mode, setMode]                  = useState<QuickCreateMode>(initialMode);
    const [assignToMe, setAssignToMe]      = useState(true);
    const [requesterEmails, setRequesterEmails] = useState<string[]>([]);
    const [requesterInput, setRequesterInput]   = useState('');
    const [requesterError, setRequesterError]   = useState('');
    const [selectedProject, selectProject] = useState<Project | undefined>(project);
    const [selectedClient, setSelectedClient] = useState<Client | undefined>(initialClient);
    const [clientError, setClientError] = useState('');

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (usersLoading === 'pending' || (users && users.length > 0))
            return;
        dispatch(loadUsers());
    }, [dispatch, users, usersLoading]);

    useEffect(()=>{
        dispatch(loadProjects());
    },[dispatch]);

    useEffect(() => {
        if (show)
            dispatch(loadClients());
    }, [dispatch, show]);

    useEffect(() => {
        if (!show)
            return;
        const emails = [
            ...(initialRequesterEmails || []),
            ...(initialClient?.email ? [initialClient.email] : []),
        ]
            .map((item) => String(item || '').trim())
            .filter(Boolean);
        const unique: string[] = [];
        emails.forEach((email) => {
            if (!unique.some((item) => item.toLowerCase() === email.toLowerCase()))
                unique.push(email);
        });
        if (unique.length)
            setRequesterEmails(unique);
        if (forcedMode)
            setMode(forcedMode);
        if (initialClient)
            setSelectedClient(initialClient);
        if (!show) {
            setClientError('');
            if (!initialClient)
                setSelectedClient(undefined);
        }
    }, [show, initialRequesterEmails, initialClient, forcedMode]);

    // Prefill the sole linked project only when none is selected yet.
    // Never overwrite a project the user explicitly chose (including unlinked ones).
    useEffect(() => {
        if (!show || !selectedClient?._id || !projects?.length || projectId)
            return;
        const matching = matchingProjectsForClient(projects, selectedClient._id);
        if (matching.length !== 1)
            return;
        setProjectId(matching[0]._id);
        selectProject(matching[0]);
    }, [show, selectedClient, projects, projectId]);

    React.useMemo(() => {
        if (show)
            dispatch(startCreateChange());
    }, [dispatch, show]);

    useEffect(() => {
        if (boardId)
            dispatch(loadBoard(boardId));
    }, [dispatch, boardId]);

    useEffect(() => {
        if (!projects?.length || !projectId)
            return;
        const match = projects.find((p: Project) => p._id === projectId);
        if (match)
            selectProject(match);
    }, [projects, projectId]);

    useEffect(() => {
        if (mode === 'support')
            setAssignToMe(true);
    }, [mode]);

    // Support: always first board of the project (no picker).
    useEffect(() => {
        if (mode !== 'support' || !selectedProject)
            return;
        const firstBoardId = selectedProject.boards?.[0]?._id || '';
        if (!firstBoardId) {
            setBoardId('');
            setColumnId('');
            return;
        }
        if (boardId !== firstBoardId) {
            setBoardId(firstBoardId);
            setColumnId('');
        }
    }, [mode, selectedProject, boardId]);

    // Support: always first column of that board (no picker).
    useEffect(() => {
        if (mode !== 'support' || !boardId)
            return;
        const embeddedColumns = selectedProject?.boards?.find((b: AnyObj) => b._id === boardId)?.columns;
        const columns = (board?._id === boardId ? board.columns : null) || embeddedColumns || [];
        const firstColumnId = columns[0]?._id || '';
        if (firstColumnId && columnId !== firstColumnId)
            setColumnId(firstColumnId);
    }, [mode, boardId, board, selectedProject, columnId]);

    const onChangeClient = (client?: Client) => {
        setSelectedClient(client);
        setClientError('');
        if (!client?._id) {
            setProjectId('');
            selectProject(undefined);
            setBoardId('');
            setColumnId('');
            return;
        }

        const matching = matchingProjectsForClient(projects, client._id);
        if (matching.length === 1) {
            setProjectId(matching[0]._id);
            selectProject(matching[0]);
            setBoardId('');
            setColumnId('');
        }
    };

    const onChangeProject = (_id: any) => {
        setProjectId(_id);
        setBoardId('');
        setColumnId('');

        if (!_id) {
            selectProject(undefined);
            return;
        }

        selectProject(projects?.find((p: Project) => p._id === _id));
    };

    const onChangeBoard = (_id: string) => {
        setBoardId(_id);
        setColumnId('');
    };

    const modeTabsRef = React.useRef<HTMLDivElement>(null);

    const onChangeMode = (next: QuickCreateMode) => {
        setMode(next);
        if (next === 'support') {
            setBoardId('');
            setColumnId('');
        }
        // Keep mode tabs in view after switching (support ↔ développement).
        requestAnimationFrame(() => {
            const tabs = modeTabsRef.current;
            if (!tabs)
                return;
            const scrollParent = tabs.parentElement;
            if (scrollParent)
                scrollParent.scrollTop = 0;
            else
                tabs.scrollIntoView({ block: 'nearest' });
        });
    };

    const addRequesterEmail = (raw: string) => {
        const email = raw.trim();
        if (!email)
            return;
        if (!isValidEmail(email)) {
            setRequesterError(t('changes.reply_invalid_email') || '');
            return;
        }
        setRequesterError('');
        setRequesterEmails((prev) => {
            if (prev.some((item) => item.toLowerCase() === email.toLowerCase()))
                return prev;
            return [...prev, email];
        });
        setRequesterInput('');
    };

    const removeRequesterEmail = (email: string) => {
        setRequesterEmails((prev) => prev.filter((item) => item !== email));
    };

    const projectClients = React.useMemo(() => (
        (selectedProject?.clients || []).filter((client: AnyObj) => client && typeof client !== 'string')
    ), [selectedProject]);

    const requesterSources = React.useMemo(() => buildRecipientEmailSources({
        users,
        clients: [...(clients || []), ...projectClients],
        changes: [...(changes || []), ...(triageChanges || []), ...(reviewChanges || [])],
        extraEmails: requesterEmails,
    }), [users, clients, projectClients, changes, triageChanges, reviewChanges, requesterEmails]);

    const createNewChange = (changeValues: AnyObj) => {
        if (!changeValues.projectId)
            return;

        const clientId = selectedClient?._id || changeValues.client?._id;
        if (!clientId) {
            setClientError(t('tracks.select_client') || '');
            return;
        }
        setClientError('');

        if (changeValues.title.length > 0) {
            let title = changeValues.title;
            let type  = mode === 'support' ? Change.TYPE_NONE : Change.TYPE_IGNORED;
            if (mode !== 'support') {
                if (changeValues.title.match(/feat: (.+)/)) {
                    title = changeValues.title.substring(6);
                    type  = Change.TYPE_FEATURE;
                } else if (changeValues.title.match(/fix: (.+)/)) {
                    title = changeValues.title.substring(5);
                    type  = Change.TYPE_FIX;
                } else if (changeValues.title.match(/ui: (.+)/)) {
                    title = changeValues.title.substring(4);
                    type  = Change.TYPE_UI;
                } else if (changeValues.title.match(/i18n: (.+)/)) {
                    title = changeValues.title.substring(6);
                    type  = Change.TYPE_I18N;
                }
            }

            const descriptionHtml = changeValues.description
                ? `<p>${String(changeValues.description).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>`
                : '';

            const pendingRequesters = parseRequesterEmails(requesterInput);
            const emails = [...requesterEmails];
            pendingRequesters.forEach((email) => {
                if (!emails.some((item) => item.toLowerCase() === email.toLowerCase()))
                    emails.push(email);
            });

            const initial: AnyObj = {
                type,
                project : changeValues.projectId,
                client: clientId,
                description: {
                    fr: descriptionHtml,
                    en: descriptionHtml,
                    de: descriptionHtml
                },
                title: {
                    fr: title,
                    en: title,
                    de: title
                }
            };

            if (mode === 'support') {
                initial.typeDomain = 'non_dev';
                initial.typeSubtype = 'support';
                if (assignToMe && user?._id) {
                    initial.assignee = { _id: user._id };
                    initial.progress = {
                        assignedAt: new Date().toISOString(),
                    };
                }
                if (emails.length) {
                    initial.replyRecipients = {
                        ...Change.emptyReplyRecipients(),
                        to: {
                            ...Change.emptyReplyRecipients().to,
                            emails,
                        },
                    };
                    initial.technicalContext = {
                        rt: { requestorEmails: emails },
                    };
                }
            }

            saveQuickCreatePlacement({
                projectId: changeValues.projectId,
                mode,
                ...(mode === 'support' ? {} : { boardId, columnId }),
            });

            if (!boardId || !columnId)
                return;

            const params = { boardId, columnId };
            dispatch(createChange(initial, params));
        }
    }

    if (created && change && change) {
        dispatch(startCreateChange());
        onAdded && onAdded(change);

        if (selectedProject && selectedProject._id === project?._id)
            dispatch(loadProject(selectedProject._id));
    }

    const projectOptions = React.useMemo(() => {
        return projectSelectOptions(projects, selectedClient?._id || initialClient?._id);
    }, [projects, selectedClient, initialClient]);

    const boardOptions = React.useMemo(() => {
        return selectedProject?.boards?.map((board: AnyObj) => ({
            value: board._id,
            label: board.name.fr
        }));
    }, [selectedProject]);

    const columnOptions = React.useMemo(() => {
        return board?.columns?.map((column: AnyObj) => ({
            value: column._id,
            label: column.title
        }));
    }, [board]);

    const titlePlaceholder = mode === 'support'
        ? (t('changes.support_title_placeholder') || '')
        : 'feat: Nouvelle fonctionnalité';

    return (
        <div className="QuickChangeModal">
            <Modal theme={modalTheme} className='bg-indigo-500 dark:bg-indigo-700 backdrop-blur-sm' dismissible show={show} size="3xl" popup onClose={onClose}>
                <Modal.Header className="w-full bg-indigo-500 dark:bg-indigo-700" theme={headerTheme}>
                    <div className="flex justify-start w-full">
                        <div className="flex flex-col justify-center w-full">
                            <div className="m-0 p-0 w-full">
                                <Icon type="plus" color="white" className="inline mr-1.5" />
                                {mode === 'support'
                                    ? t('changes.add_support_ticket')
                                    : t('projects.add_card')}
                            </div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="flex flex-col px-4 py-6 sm:px-8">
                    <div
                        ref={modeTabsRef}
                        className="mb-4 sticky top-0 z-10 -mx-4 px-4 sm:-mx-8 sm:px-8 pt-1 pb-2 bg-white dark:bg-gray-700"
                    >
                        <div className="flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <button
                                type="button"
                                className={`flex-1 px-3 py-2 text-sm font-medium ${mode === 'card' ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                onClick={() => onChangeMode('card')}
                            >
                                <Icon type="feature" className="inline mr-1.5" size={3} />
                                {t('changes.quick_mode_card')}
                            </button>
                            <button
                                type="button"
                                className={`flex-1 px-3 py-2 text-sm font-medium border-l border-gray-200 dark:border-gray-700 ${mode === 'support' ? 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                onClick={() => onChangeMode('support')}
                            >
                                <Icon type="ticket" className="inline mr-1.5" size={3} />
                                {t('changes.quick_mode_support')}
                            </button>
                        </div>
                    </div>
                    <Formik
                        enableReinitialize
                        initialValues={{projectId, boardId, columnId, client: initialClient, title: '', description: ''}}
                        onSubmit={(values: {}) => createNewChange(values)}
                    >
                        {({ setFieldValue }) => (
                        <Form className='h-full'>
                            <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                <Icon type="user" className="inline mr-1.5" />
                                {t('tracks.client')}
                            </label>
                            <ClientSelectField
                                name="client"
                                placeholder={t('tracks.select_client') || ''}
                                allowClear={!initialClient}
                                autoFocus={!initialClient}
                                value={selectedClient}
                                onChange={(client) => {
                                    setFieldValue('client', client || null, false);
                                    if (!client) {
                                        setFieldValue('projectId', '', false);
                                        setFieldValue('boardId', '', false);
                                        setFieldValue('columnId', '', false);
                                    }
                                    onChangeClient(client);
                                }}
                            />
                            {clientError && (
                                <p className="mt-1 text-xs text-red-600">{clientError}</p>
                            )}
                            {selectedClient && (
                                <div className="mt-5">
                                    <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                        <Icon type="project" className="inline mr-1.5" />
                                        {t('changes.choose_a_project')}
                                    </label>
                                    <SelectField
                                        name="projectId"
                                        onChange={onChangeProject}
                                        placeholder={t('changes.choose_a_project') || ''}
                                        options={projectOptions}
                                    />
                                </div>
                            )}
                            {selectedClient && selectedProject && mode !== 'support' && (
                                <div className='mt-5'>
                                    {boardOptions && boardOptions.length > 0 ? (
                                        <>
                                            <LabelWithHelp help={t('changes.help.quick_board') || ''} className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                <span className="inline-flex items-center">
                                                    <Icon type="list" className="inline mr-1.5" />
                                                    {t('changes.choose_a_board')}
                                                </span>
                                            </LabelWithHelp>
                                            <SelectField
                                                name="boardId"
                                                onChange={onChangeBoard}
                                                placeholder={t('changes.choose_a_board') || ''}
                                                options={boardOptions}
                                            />
                                        </>
                                    ) : (
                                        <span className="block text-sm italic font-bold text-center">
                                            {t('changes.this_project_has_no_column')}
                                        </span>
                                    )}
                                </div>
                            )}
                            {mode !== 'support' && boardId && (board?._id === boardId) && (
                                <div className='mt-5'>
                                    {columnOptions?.length > 0 ? (
                                        <>
                                            <LabelWithHelp help={t('changes.help.quick_column') || ''} className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                <span className="inline-flex items-center">
                                                    <Icon type="list" className="inline mr-1.5" />
                                                    {t('changes.choose_a_column')}
                                                </span>
                                            </LabelWithHelp>
                                            <SelectField
                                                name="columnId"
                                                onChange={setColumnId}
                                                placeholder={t('changes.choose_a_column') || ''}
                                                options={columnOptions}
                                            />
                                        </>
                                    ) : (
                                        <span className="block text-sm italic font-bold text-center">
                                            {t('changes.this_project_has_no_column')}
                                        </span>
                                    )}
                                </div>
                            )}
                            {mode === 'support' && selectedClient && selectedProject && !selectedProject.boards?.length && (
                                <span className="mt-5 block text-sm italic font-bold text-center">
                                    {t('changes.this_project_has_no_column')}
                                </span>
                            )}
                            {selectedClient && columnId && (
                                <div className='mt-5'>
                                    <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                        <Icon type="edit" className="inline mr-1.5" />
                                        {mode === 'support' ? t('changes.enter_subject') : t('changes.enter_title')}
                                    </label>
                                    <Field
                                        type="text"
                                        name="title"
                                        required
                                        autoFocus={!!initialClient}
                                        placeholder={titlePlaceholder}
                                        className="mb-3 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                    />
                                    {mode === 'support' && (
                                        <div className="mb-4 space-y-3">
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-600"
                                                    checked={assignToMe}
                                                    onChange={(e) => setAssignToMe(e.target.checked)}
                                                    disabled={!user?._id}
                                                />
                                                {t('changes.assign_to_me')}
                                            </label>
                                            <div>
                                                <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                    <Icon type="email" className="inline mr-1.5" />
                                                    {t('changes.requesters')}
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                    {t('changes.requesters_hint')}
                                                </p>
                                                <EmailSuggestField
                                                    value={requesterInput}
                                                    onChange={(next) => {
                                                        setRequesterInput(next);
                                                        setRequesterError('');
                                                    }}
                                                    onCommit={addRequesterEmail}
                                                    sources={requesterSources}
                                                    excludedEmails={requesterEmails}
                                                    placeholder={t('changes.requesters_placeholder') || ''}
                                                    allowMulti
                                                    inputClassName="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                                />
                                                {requesterError && (
                                                    <p className="mt-1 text-xs text-red-600">{requesterError}</p>
                                                )}
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    {requesterEmails.map((email) => (
                                                        <span
                                                            key={email}
                                                            className="inline-flex items-center rounded bg-blue-100 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100 px-2 py-1 text-xs"
                                                        >
                                                            {email}
                                                            <button
                                                                className="ml-1 inline-flex text-current"
                                                                type="button"
                                                                aria-label={t('common.delete') || ''}
                                                                onClick={() => removeRequesterEmail(email)}
                                                            >
                                                                <Icon type="close" size={3} color="inherit" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <label className="block font-medium leading-6 text-gray-900 dark:text-gray-100 mt-5">
                                        <Icon type="comment" className="inline mr-1.5" />
                                        {t('changes.describe')}
                                    </label>
                                    <Field
                                        component="textarea"
                                        name="description"
                                        rows="3"
                                        className="mb-3 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            )}
                            {selectedClient && columnId && (
                                <div className='mt-5 flex justify-center'>
                                    <Button
                                        type="submit"
                                        title={t('common.save')}
                                        color="primary"
                                        icon={creating === 'pending' ? 'loading' : 'save'}
                                    />
                                </div>
                            )}
                        </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default QuickChangeModal;
